import type { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';
import type { Location } from 'vue-router';
import type { CombinedVueInstance, Vue } from 'vue/types/vue';
import type { OnboardingContext, OnboardingStep } from '@/helpers/onboarding';

import { mapGetters } from 'vuex';
import OnboardingChoiceButton from '@/components/onboarding/OnboardingChoiceButton.vue';
import OnboardingFormStep from '@/components/onboarding/OnboardingFormStep.vue';
import OnboardingStepComponent from '@/components/onboarding/OnboardingStep.vue';

const onboarding: ThisTypedComponentOptionsWithRecordProps<
  Vue,
  OnboardingMixinData,
  OnboardingMixinMethods,
  OnboardingMixinComputed,
  OnboardingMixinProps
> = {
  components: {
    OnboardingChoiceButton,
    OnboardingFormStep,
    OnboardingStep: OnboardingStepComponent,
  },
  computed: {
    ...mapGetters('onboarding', [
      'candidateCount',
      'currentStep',
      'currentStepIndex',
      'event',
      'hiringUserProfile',
      'isAlreadyOnboarded',
      'isIncompleteProfileRedirect',
      'isNewAccount',
      'isStudio',
      'isUpgrading',
      'nextRoute',
      'nextStep',
      'originalPlan',
      'plan',
      'planId',
      'previousRoute',
      'previousStep',
      'profile',
      'steps',
      'user',
    ]),
    billingPeriod: {
      get() {
        return this.$store.getters['onboarding/billingPeriod'];
      },
      set(value) {
        this.$store.commit('onboarding/setBillingPeriod', value);
      },
    },
    debug: {
      get() {
        return this.$store.getters['onboarding/debug'];
      },
      set(value) {
        this.$store.commit('onboarding/setDebug', value);
      },
    },
  },
};

export default onboarding;

export type OnboardingComponent = CombinedVueInstance<
  Vue,
  OnboardingMixinData,
  OnboardingMixinMethods,
  OnboardingMixinComputed,
  OnboardingMixinProps
>;

/**
 * A Vue component that has access to the current onboarding process.
 */
export interface OnboardingMixin extends Vue, OnboardingContext {
  /**
   * The selected billing period.
   */
  readonly billingPeriod: BillingPeriod;
  /**
   * The number of candidates that match the {@link OnboardingMixin.event `event`} parameters (if
   * specified).
   */
  readonly candidateCount: number | null;
  /**
   * The current onboarding step, or `null` if there is no current step.
   */
  readonly currentStep: OnboardingStep | null;
  /**
   * The zero-based index of the current onboarding step within the current flattened
   * onboarding path, or `null` if there is no current step.
   */
  readonly currentStepIndex: number | null;
  /**
   * `true` if the onboarding flow is in debug mode; otherwise, `false`.
   */
  debug: boolean;
  /**
   * The location to which the user will be routed after completing the current step.
   */
  readonly nextRoute: Readonly<Location>;
  /**
   * The next onboarding step, or `null` if there is no next step.
   */
  readonly nextStep: OnboardingStep | null;
  /**
   * The location to which the user will be routed upon clicking the "Back" button (if one is
   * present).
   */
  readonly previousRoute: Readonly<Location> | null;
  /**
   * The previous onboarding step, or `null` if there is no previous step.
   */
  readonly previousStep: OnboardingStep | null;
  /**
   * An array containing all steps in the current onboarding path.
   */
  readonly steps: readonly OnboardingStep[];
}

export interface OnboardingMixinComputed
  extends Pick<
    OnboardingMixin,
    Exclude<keyof OnboardingMixin, keyof OnboardingMixinMethods | keyof Vue>
  > {}

export interface OnboardingMixinData {}

export type OnboardingMixinMethods = Pick<
  OnboardingMixin,
  {
    [K in keyof OnboardingMixin]-?: K extends keyof Vue
      ? never
      : OnboardingMixin[K] extends Function
      ? K
      : never;
  }[keyof OnboardingMixin]
>;

export interface OnboardingMixinProps {}
